/**
 * 上传文件到七牛 CDN
 */
import Request from "@src/common/request";
import Helper from "@src/common/helper/core";
import md5 from 'md5';
import CryptoJS from 'crypto-js';
import * as qiniu from 'qiniu-js';
import EE from "../event";

let cdnOrigin = "";

// 文件大于一定大小时，不计算 sha1
export const MAX_SIZE_FOR_SHA1 = 80 * 1024 * 1024;

/**
 * 文件是否已存在
 * @param {String} url 七牛 url
 */
export const isFileExisted = (url) => {
  if (!url) {
    return Promise.resolve(false);
  }

  return Request.head(url, {
    withCredentials: false,
  })
  .then(() => true)
  .catch((res) => {
    // debug request 太业务化 获取不到具体信息 不能判断是没上传过还是已被删除
    // console.error(`检查文件是否已存在报错`, res);
    return false;
  });
}

/**
 * 获取文件基本信息
 * @param {File} file 
 * @returns 
 *  {
 *    filename,
 *    type,
 *    width,
 *    height,
 *    size,
 *    duration,
 *  }
 */
export const getFileInfo = (file) => {
  return new Promise((resolve) => {
    let type = 'file';
    if (/^(image|video)/.test(file.type)) {
      const typeSpiece = file.type.split("/");
      type = typeSpiece[0];
    }

    if (type == "file") {
      resolve({
        filename: file.name,
        type,
        width: 0,
        height: 0,
        size: file.size,
      });
      return;
    }

    Helper[type == "image" ? "getImageInfo" : "getVideoInfo"](file)
    .then(info => {
      resolve({
        filename: file.name,
        type,
        width: info.width,
        height: info.height,
        size: file.size,
        ...(
          type == "image"
          ? {}
          : {
            duration: info.duration,
          }
        )
      });
    })
  });
}

/**
 * 获取文件上传信息
 * @param {File} file 
 * @returns 
 *  {
 *    key,
 *  }
 */
export const getUploadInfo = (file) => {
  const size = file.size;
  // ext 扩展名
  let ext = Helper.getFileNameExt(file.name);
  if (/^image|video/.test(file.type)) {
    const typeSpiece = file.type.split("/");
    ext = (typeSpiece[typeSpiece.length - 1]).replace("quicktime", "mov");
  }

  // 大文件不计算 sha1，自测超 200M 会报错，越大越耗时
  if (size > MAX_SIZE_FOR_SHA1) {
    // key
    const key = `webbigfile${md5(Helper.uniqueId())}`;
    // 上传 key
    const upKey = ext ? `${key}.${ext}` : key;

    return Promise.resolve({
      key: upKey,
      ext,
    });
  }
  else {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        // key
        const arrayBuffer = event.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        const list = [];
        for (let i = 0; i < uint8Array.length; i += 4) {
          list.push(uint8Array[i] << 24 | uint8Array[i + 1] << 16 | uint8Array[i + 2] << 8 | uint8Array[i + 3]);
        }
        const wordArray = CryptoJS.lib.WordArray.create(list, uint8Array.length);
        const key = CryptoJS.SHA1(wordArray).toString();

        // 上传 key
        const upKey = ext ? `${key}.${ext}` : key;

        resolve({
          // 上传的文件名，给七牛的 key
          key: upKey,
          ext,
        });
      };
      reader.readAsArrayBuffer(file);
    });
  }
}

/**
  * 获取文件上传 token
  * @param {Object} infos
  * @example
  *  getUploadToken([
  *    {
  *      key : "xxx.png",
  *    },
  *  ])
  *  .then(tokens => {
  *    tokens = {
  *      "xxx.png" : "yyy",
  *    }
  *  })
  */
export const getUploadToken = (infos) => {
  if (!infos || !infos.length) {
    return Promise.resolve({});
  }
  const filename = {};
  infos.forEach(({ key }, index) => {
    filename[index] = key;
  });
  // 上传任意文件
  return Request.post("/qiniu/upothers", {
    filename,
  })
    .then(res => {
      const { data = {} } = res;
      cdnOrigin = data.domain;
      return data.token;
    })
    .catch(() => {
      return {};
    });
}

/**
 * 上传文件
 * @param {File Array} fileList 
 * @param {Object} callbacks 
 * @callbacks
 *    success({ 
 *      file, // 文件
 *      filename, // 文件名
 *      size, // 大小
 *      type, // 文件类型 video/image/file(非 video/image 的都为 file)
 *      key, // 上传七牛的 key
 *      url, // 上传后的 url
 *      width, // 尺寸(非 video/image 时为 0)
 *      height,
 * 
 *      cover, // 视频封面地址
 *      duration, // 视频时长
 *    });
 * 
 *    fail({
 *      file, // 文件
 *      filename, // 文件名
 *      error, // 错误信息(如有)
 *    });
 * 
 *    begin({
 *      file, // 文件
 *      filename, // 文件名
 *      task, // 上传任务 可用于取消上传 task.unsubscribe();
 *    });
 * 
 *    progress({
 *      file, // 文件
 *      filename, // 文件名
 *      percent, // 上传进度
 *    })
 * 
 *    complete({
 *      success : [], // 格式同 success
 *      fail : [], // 格式同 fail
 *    });
 */
export const upload = (fileList, callbacks = {}) => {
  const {
    success = () => { }, // 单个文件上传成功
    fail = () => { }, // 单个文件上传失败
    begin = () => { }, // 单个文件开始上传，返回上传 task，可用于取消上传
    progress = () => { }, // 单个文件上传进度 x%
    complete = () => { }, // 所有文件上传完成(成功或失败)
  } = callbacks;

  // 输入有误
  if (!fileList?.length) {
    complete({
      success: [],
      fail: [],
    });
    return;
  }

  const inputLength = fileList.length;
  fileList = Array.from({ length: inputLength }).map((item, index) => fileList[index]);
  const successRes = [];
  const failRes = [];

  Promise.all(fileList.map(file => {
    return new Promise((resolve) => {
      Promise.all([getFileInfo(file), getUploadInfo(file)])
      .then(infos => {
        const info = Object.assign.apply({}, infos);
        resolve({
          file,
          ...info,
        });
      });
    })
  }))
  .then(infos => {
    // 基本信息过滤
    let upList = infos.filter(info => {
      // 视频时长过滤
      if (info.type == "video" && info.duration < 1) {
        const res = {
          file: info.file,
          filename: info.filename,
          error: "不得上传长度小于 1 秒的视频"
        };
        fail(res);
        failRes.push(res);
        return false;
      }

      // 校验必要的字段
      if (["key"].some(key => !info[key])) {
        const res = {
          file: info.file,
          filename: info.filename,
          error: "文件信息缺少必要的字段",
          info,
        };
        fail(res);
        failRes.push(res);
        return false;
      }

      return true;
    });

    // 全部完成
    if (!upList.length) {
      complete({
        success: successRes,
        fail: failRes,
      });
      return;
    }

    // 获取 token
    getUploadToken(upList)
    .then(tokens => {
      upList = upList.map(info => {
        const { key } = info;
        const token = tokens[key];
        if (!token) {
          const res = {
            file: info.file,
            filename: info.filename,
            error: "获取 token 失败",
          };
          fail(res);
          failRes.push(res);
          return false;
        }

        const url = `${cdnOrigin}/${info.key}`;
        return {
          ...info,
          ...{
            token,
            url,
          },
          ...(
            info.type == "video"
              ? {
                cover: `${url}?vframe/jpg/offset/1`,
              }
              : {}
          )
        }
      }).filter(_ => _);

      // 全部完成
      if (!upList.length) {
        complete({
          success: successRes,
          fail: failRes,
        });
        return;
      }

      // 判断是否已上传过
      Promise.all(upList.map(info => isFileExisted(info.url)))
      .then(uploadedResList => {
        upList = upList.filter((info, index) => {
          if (uploadedResList[index]) {
            begin({
              file : info.file,
              filename: info.filename,
              task : null,
              ext : info.ext,
            });

            delete (info.token);
            success(info);
            successRes.push(info);
            return false;
          }
          return true;
        });

        // 全部完成
        if (!upList.length) {
          complete({
            success: successRes,
            fail: failRes,
          });
          return;
        }

        // 开始上传
        const checkComplete = () => {
          if (inputLength == successRes.length + failRes.length) {
            complete({
              success: successRes,
              fail: failRes,
            });
          }
        }
        upList.forEach(info => {
          const { file, key, token, ext } = info;
          const observableRes = qiniu.upload(file, key, token);
          const subscription = observableRes.subscribe(
            // 上传进度
            res => {
              progress({
                file,
                filename: info.filename,
                percent: res.total.percent,
                ext,
              });
            },
            // 上传失败
            res => {
              const resError = {
                file,
                filename: info.filename,
                error: res,
                key: info.key,
                url: info.url,
                ext,
              }
              fail(resError);
              failRes.push(resError);
              checkComplete();
            },
            // 上传成功
            res => {
              delete (info.token);
              info.isNew = true;
              success(info);
              successRes.push(info);
              checkComplete();
            },
          );
          begin({
            file,
            filename: info.filename,
            task: subscription,
            ext,
          });
        });
      })
    });
  })
}

/**
 * 调服务端接口上传 base64
 * @param {String} base64 
 * @param {?Object} params 
 */
export const uploadBase64 = (base64, params = {}) => {
  return Request.post("/resource/base64", {
    data: base64,
    ...params,
  })
  .then(res => {
    return res.data.url;
  })
  .catch(res => {
    return null;
  });
}

/**
 * 获取上传组件配置项
 * @param {Object} config 
 */
const acceptList = {
  ppt : [
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ],
  word : [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  pdf : [
    "application/pdf",
  ],
  excel : [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
}
export const getUploadConfig = (config = {}) => {
  if(config.accepts || !config.accept){
    let accept = [];
    config.accepts.forEach(type => {
      const list = acceptList[type] || [];
      accept = [...accept, ...list];
    });
    config.accept = accept.join(",");
    delete(config.accepts);
  }
  const scene = config.scene || 'common';

  return {
    ...{
      multiple: true,
      showUploadList: false,
      accept: "*",
      customRequest: (event) => {
        Helper.log(`customRequest event`, event);

        const { file } = event;
        const uploadKey = file.uid;
        const { name:filename } = file;
        const ext = Helper.getFileNameExt(filename);
        const callbackParams = {
          scene,
          uploadKey,
          filename,
          ext,
        }
  
        upload([file], {
          begin : ({ task }) => {
            EE.emit("update-file-upload", {
              type : "begin",
              task,
              size : file.size,
              ...callbackParams,
            });
          },
          progress : ({ percent }) => {
            // debug
            // Helper.log(`文件 ${filename} 已上传: ${percent.toFixed(1)}%`);
            
            EE.emit("update-file-upload", {
              type : "percent",
              percent,
              ...callbackParams,
            });
          },
          complete : ({success, fail}) => {
            // debug
            // Helper.log(`文件 ${filename} 上传结果`, success, fail);

            const data = success[0];
            if(!data){
              EE.emit("update-file-upload", {
                type : "error",
                ...callbackParams,
              })
              return;
            }
            EE.emit("update-file-upload", {
              type : "success",
              ...callbackParams,
              // 上传成功时，抛出 url
              ...Helper.copyObj(data, ["url"]),
            })
          },
        });
      },
  
      beforeUpload: (file) => {
        Helper.log(`beforeUpload file`, file);
  
        // 文件大小限制
        if(config.size_limit && file.size > config.size_limit){
          EE.emit("update-file-upload-error", {
            // eslint-disable-next-line
            uploadKey,
            // eslint-disable-next-line
            filename,
            msg : `文件大小不得超过${Math.ceil(config.size_limit / 1024 / 1024)}M`,
          })
          return false;
        }
        return true;
      },
    },
    ...config,
  }
}